import { createI18n } from 'vue-i18n';
import type { I18n } from 'vue-i18n';
import _ from 'lodash';

// Agentic 本地语言包
import cnMessages from './locales/cn.json';
import enMessages from './locales/en.json';
import jpMessages from './locales/jp.json';
import twMessages from './locales/tw.json';

// Shared 通用错误码语言包
import sharedCnMessages from '@eureka/shared/src/locales/common-errorcode/cn.json';
import sharedEnMessages from '@eureka/shared/src/locales/common-errorcode/en.json';
import sharedJpMessages from '@eureka/shared/src/locales/common-errorcode/jp.json';
import sharedTwMessages from '@eureka/shared/src/locales/common-errorcode/tw.json';

/**
 * 支持的语言类型
 */
type LocaleType = 'cn' | 'en' | 'jp' | 'tw';

/**
 * 全局 i18n 对外暴露的轻量 Composition API 类型。
 *
 * 语言包会按功能动态合并，因此这里不展开 JSON 的全量键路径；
 * 同时固定 legacy=false，保证 global 始终是可调用 t 的 Composer。
 */
type AgenticI18n = I18n<
    Record<LocaleType, Record<string, unknown>>,
    Record<string, never>,
    Record<string, never>,
    LocaleType,
    false
>;

/**
 * 合并语言包
 * @param localMessages - 本地语言包
 * @param sharedMessages - 共享语言包
 */
const mergeMessages = (localMessages: Record<string, any>, sharedMessages: Record<string, any>) => {
    return _.merge({}, sharedMessages, localMessages);
};

const pattern = /---uniform-setting-locale=(\w+)/;

export const getDefaultLocale = () => {
    const [, lang] = document.cookie.match(pattern) || [];
    if (!lang) {
        const host = window.location.host || '';
        if (host.indexOf('.zhihuiya.') > -1) {
            return 'cn';
        } else if (host.indexOf('.patsnap.') > -1) {
            return 'en';
        }
    }
    return lang || 'en';
};

/**
 * 创建 i18n 实例
 */
const createI18nInstance = (locale?: LocaleType): AgenticI18n => {
    const defaultLocale = locale || getDefaultLocale();

    return createI18n({
        legacy: false, // 使用 Composition API 模式
        locale: defaultLocale,
        fallbackLocale: 'en',
        messages: {
            cn: mergeMessages(cnMessages, sharedCnMessages),
            en: mergeMessages(enMessages, sharedEnMessages),
            jp: mergeMessages(jpMessages, sharedJpMessages),
            tw: mergeMessages(twMessages, sharedTwMessages),
        },
        missingWarn: false,
        fallbackWarn: false,
    }) as unknown as AgenticI18n;
};

/**
 * 全局 i18n 实例
 */
export const i18n = createI18nInstance();

/**
 * 添加语言包
 * @param locale - 语言代码
 * @param messages - 语言消息
 */
export const addLocaleMessages = (locale: LocaleType, messages: Record<string, any>): void => {
    i18n.global.mergeLocaleMessage(locale, messages);
};
